import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  ChipProps,
  Popover,
  Stack,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { Circle as CircleIcon, Info as InfoIcon } from '@mui/icons-material';

import { useLocation } from 'react-router-dom';
import { TP_COLORS } from '../../../../constants';
import { ISection, LESSON_TYPES } from '../../../../store/V2/SectionSlice';
import { getCoursesState } from '../../../../store/V2/CoursesSlice';
import {
  TZoomMeetingRequestStatus,
  ZOOM_MEETING_REQUEST_STATUSES,
  ZOOM_MEETING_REQUEST_TYPES,
} from '../../../../store/V2/ZoomRequestsSlice';
import {
  TClassJoinStatusData,
  getClassJoinStatusData,
  getClassShortUrlByWaitingId,
} from '../../../../helper';
import { useAppSelector } from '../../../../hooks';
import { ILessonHistory } from '../../../../store/V2/LessonHistorySlice';
import ViewZoomRecordingsModal from '../../../../components/Modals/ViewZoomRecordingsModal';
import ClassJoinTimeStatusViewerPopperComponent from './ClassJoinTimeStatusViewerPopperComponent';
import ZoomMeetingOptionsButtonWithMenu from './ZoomMeetingOptionsButtonWithMenu';
import { IDENTIFIED_USER_ROLES } from '../../../../store/UserSlice';
import StudentFeedbackModal from '../../../../components/V2/Feedback/StudentFeedbackModal';
import TutorFeedbackModal from '../../../../components/V2/Feedback/TutorFeedbackModal';

interface ZoomSectionItemProps {
  orderNumber?: number;
  section: ISection;
  innerContentOnly?: boolean;
  isCourseSubscribed?: boolean;
  isOneToOne: boolean;
  onCancelClass: (section: ISection) => void;
  onRescheduleClass: (section: ISection) => void;
}

const classLinkButtonStyles: SxProps = {
  backgroundColor: '#3498DB',
  color: TP_COLORS.white,
  p: '8px 12px 8px 12px',
  borderRadius: '5px',
  fontSize: '12px',
  fontWeight: 600,
};

const containerStyles: SxProps = {
  backgroundColor: 'transparent',
  borderRadius: '10px',
  border: '1px dashed #CBD5E1',
};

const animationStyles: SxProps = {
  position: 'absolute',
  top: '80%',
  transform: 'translateY(-50%)',
  width: '12px',
  height: '12px',
  border: '2px solid black',
  borderRadius: '50%',
  zIndex: 1,
  animation: 'wave-animation 1.5s infinite',
  '@keyframes wave-animation': {
    '0%': {
      transform: 'scale(1)',
      opacity: 0.8,
    },
    '25%': {
      transform: 'scale(1.2)',
      opacity: 0.7,
    },
    '50%': {
      transform: 'scale(1.3)',
      opacity: 0.5,
    },
    '75%': {
      transform: 'scale(1.4)',
      opacity: 0.4,
    },
    '100%': {
      transform: 'scale(1.5)',
      opacity: 0.3,
    },
  },
};

export const getZoomRequestStatusColor = (
  status: TZoomMeetingRequestStatus
): ChipProps['color'] => {
  let color: ChipProps['color'] = 'default';

  switch (status) {
    case ZOOM_MEETING_REQUEST_STATUSES.APPROVED:
      color = 'success';
      break;

    case ZOOM_MEETING_REQUEST_STATUSES.REJECTED:
      color = 'error';
      break;

    case ZOOM_MEETING_REQUEST_STATUSES.PENDING:
      color = 'warning';
      break;

    default:
      color = 'default';
      break;
  }

  return color;
};

const CustomCircleIcon = (
  <CircleIcon sx={{ width: '7px', height: '7px', color: '#CBD5E1' }} />
);

const ZoomSectionItem: React.FC<ZoomSectionItemProps> = ({
  orderNumber,
  section,
  isOneToOne,
  innerContentOnly,
  isCourseSubscribed = false,
  onCancelClass,
  onRescheduleClass,
}) => {
  // Store
  const coursesState = useAppSelector(getCoursesState);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const scrollId = searchParams.get('scroll');

  // State
  const [isTutorFeedbackModalOpen, setTutorFeedbackModalOpen] = useState(false);
  const [iconAnchorEl, setIconAnchorEl] = useState<HTMLElement | null>(null);
  const [isTooltipOpen, setTooltipOpen] = useState(true);

  const [
    isViewZoomRecordingsModalOpen,
    setViewZoomRecordingsModalOpen,
  ] = useState(false);

  const [isStudentFeedbackModalOpen, setStudentFeedbackModalOpen] = useState(
    false
  );

  const lessonHistory = section.lessonHistory as ILessonHistory | undefined;
  const courseData = coursesState.userCourse;
  const popOverId = `${section._id}-mouse-over-popover`;
  const isPopOverOpen = Boolean(iconAnchorEl);

  let classLink = '';
  let formatedDuration = '';
  let studentJoinData: TClassJoinStatusData | undefined;
  let tutorJoinData: TClassJoinStatusData | undefined;
  let joinData;
  let isJoinDataVisible = false;

  const formatedDateTime = section.dateTime
    ? moment(section.dateTime).format('ddd MMM DD YYYY hh:mm A')
    : '';

  if (section.duration) {
    const _hours = Math.floor(section.duration / 60);
    const _mins = section.duration % 60;

    if (_hours) {
      formatedDuration = `${_hours} hr`;

      if (_mins) {
        formatedDuration += ` ${_mins} min`;
      }
    } else {
      formatedDuration += ` ${_mins} min`;
    }
  }

  if (section.dateTime) {
    studentJoinData = getClassJoinStatusData(
      section.dateTime,
      section.studentJoinTime
    );

    tutorJoinData = getClassJoinStatusData(
      section.dateTime,
      section.tutorJoinTime
    );
  }

  if (courseData && courseData.isSubscribed) {
    isJoinDataVisible = true;

    const isZoomRequestCanceled =
      section.zoomRequest &&
      section.zoomRequest.type === ZOOM_MEETING_REQUEST_TYPES.CANCEL &&
      section.zoomRequest.status === ZOOM_MEETING_REQUEST_STATUSES.APPROVED;

    const isZoomRequestNotPending = !(
      section.zoomRequest &&
      section.zoomRequest.status === ZOOM_MEETING_REQUEST_STATUSES.PENDING
    );

    const isClassPassed =
      section.dateTime &&
      section.duration &&
      moment(section.dateTime).add(section.duration, 'minutes') < moment();

    if (
      section.classWaitingId &&
      !isZoomRequestCanceled &&
      isZoomRequestNotPending &&
      !isClassPassed
    ) {
      classLink = getClassShortUrlByWaitingId(section.classWaitingId);
    }

    if (courseData.identifiedCourseUserRole === IDENTIFIED_USER_ROLES.STUDENT) {
      joinData = studentJoinData;
    } else if (
      courseData.identifiedCourseUserRole === IDENTIFIED_USER_ROLES.TUTOR
    ) {
      joinData = tutorJoinData;
    }
  }

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setTooltipOpen(false);
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const toggleViewZoomRecordingsModal = () => {
    setViewZoomRecordingsModalOpen((prevValue) => !prevValue);
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setIconAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setIconAnchorEl(null);
  };

  const toggleStudentFeedbacksModal = () => {
    setStudentFeedbackModalOpen((prevValue) => !prevValue);
  };

  const toggleTutorFeedbacksModal = () => {
    setTutorFeedbackModalOpen((prevValue) => !prevValue);
  };

  const renderInnerContent = () => {
    return (
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Typography fontWeight={400} fontSize="14px" color="#1E293B">
          {formatedDateTime}
        </Typography>
        {CustomCircleIcon}
        <Typography fontWeight={400} fontSize="14px" color="#1E293B">
          <b>Duration:</b> {formatedDuration}
        </Typography>
        {lessonHistory &&
        courseData &&
        courseData.identifiedCourseUserRole &&
        courseData.identifiedCourseUserRole ===
          IDENTIFIED_USER_ROLES.STUDENT ? (
          <>
            {CustomCircleIcon}
            <ButtonBase
              onClick={toggleStudentFeedbacksModal}
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#01A79F',
                textDecoration: 'underline',
              }}
            >
              Feedbacks
            </ButtonBase>
          </>
        ) : null}
        {lessonHistory &&
        lessonHistory._id &&
        courseData &&
        courseData.identifiedCourseUserRole &&
        courseData.identifiedCourseUserRole === IDENTIFIED_USER_ROLES.TUTOR ? (
          <>
            {CustomCircleIcon}
            <ButtonBase
              onClick={toggleTutorFeedbacksModal}
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#01A79F',
                textDecoration: 'underline',
              }}
            >
              Feedbacks
            </ButtonBase>
          </>
        ) : null}
        {section.zoomRecordings && section.zoomRecordings.length ? (
          <>
            {CustomCircleIcon}
            <ButtonBase
              onClick={toggleViewZoomRecordingsModal}
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#1E293B',
                textDecoration: 'underline',
              }}
            >
              Recordings
            </ButtonBase>
          </>
        ) : null}
        {isJoinDataVisible &&
        section.dateTime &&
        moment(section.dateTime) < moment() ? (
          <>
            {CustomCircleIcon}
            <Typography
              fontSize="14px"
              fontWeight={500}
              color={joinData && joinData.color}
              display="flex"
              alignItems="center"
            >
              {joinData && joinData.status ? joinData.status : '-'}
              <>
                &nbsp;
                <span
                  aria-owns={isPopOverOpen ? popOverId : undefined}
                  aria-haspopup="true"
                  onMouseEnter={handlePopoverOpen}
                  onMouseLeave={handlePopoverClose}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <InfoIcon
                    sx={{
                      color: '#000000',
                      width: '15px',
                      height: '15px',
                    }}
                  />
                </span>
              </>
            </Typography>
          </>
        ) : null}
        {isStudentFeedbackModalOpen && lessonHistory ? (
          <StudentFeedbackModal
            open={isStudentFeedbackModalOpen}
            onClose={toggleStudentFeedbacksModal}
            lessonHistoryId={lessonHistory._id}
          />
        ) : null}
        {isTutorFeedbackModalOpen && lessonHistory ? (
          <TutorFeedbackModal
            open={isTutorFeedbackModalOpen}
            onClose={toggleTutorFeedbacksModal}
            lessonHistoryId={lessonHistory._id}
          />
        ) : null}
      </Stack>
    );
  };

  return (
    <>
      {innerContentOnly ? (
        renderInnerContent()
      ) : (
        <Card elevation={0} sx={containerStyles}>
          <CardContent sx={{ p: '16px' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center" flexGrow={1} mr="15px">
                <Avatar
                  sx={{
                    width: '36px',
                    height: '36px',
                    backgroundColor: isCourseSubscribed ? '#CBD5E1' : '#1C9FF7',
                    color: '#F8FAFC',
                    mr: '15px',
                  }}
                >
                  {orderNumber}
                </Avatar>
                <Stack gap={1}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Chip
                      size="small"
                      label="Zoom"
                      color="info"
                      sx={{ width: 'fit-content' }}
                    />
                    {section.zoomRequest && isCourseSubscribed ? (
                      <Chip
                        size="small"
                        label={`${section.zoomRequest.type} - ${section.zoomRequest.status}`}
                        color={getZoomRequestStatusColor(
                          section.zoomRequest.status
                        )}
                        sx={{ width: 'fit-content' }}
                      />
                    ) : null}
                  </Stack>

                  <Typography
                    title={section.title}
                    variant="subtitle2"
                    fontWeight={600}
                    className="ellipsis-text one-line"
                    maxWidth="calc(100% - 20px)"
                  >
                    {section.title}
                  </Typography>
                  {renderInnerContent()}
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                {classLink && section.lessonType !== LESSON_TYPES.DUMMY ? (
                  <Tooltip
                    title={section._id === scrollId ? 'Join Now' : ''}
                    placement="bottom"
                    arrow
                    open={isTooltipOpen}
                    disableInteractive
                    PopperProps={{
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 9],
                          },
                        },
                      ],
                      sx: {
                        '.MuiTooltip-tooltip': {
                          backgroundColor: '#1E293B',
                          color: 'white',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          fontSize: '13px',
                          '.MuiTooltip-arrow': {
                            color: '#1E293B',
                          },
                        },
                      },
                    }}
                  >
                    <ButtonBase
                      id={`${section._id}`}
                      href={classLink}
                      target="_blank"
                      sx={{
                        ...classLinkButtonStyles,
                        position: 'relative',
                      }}
                      onMouseEnter={handleTooltipOpen}
                    >
                      JOIN NOW
                      {section._id === scrollId && <Box sx={animationStyles} />}
                    </ButtonBase>
                  </Tooltip>
                ) : null}

                {/* CLASS OPTIONS MENU BUTTON */}
                {courseData &&
                courseData.isSubscribed &&
                section.lessonType !== LESSON_TYPES.DUMMY ? (
                  <ZoomMeetingOptionsButtonWithMenu
                    section={section}
                    onCancelClass={onCancelClass}
                    isOneToOne={isOneToOne}
                    onRescheduleClass={onRescheduleClass}
                  />
                ) : null}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}
      {/* Modals */}
      <Popover
        disableRestoreFocus
        id={popOverId}
        open={isPopOverOpen}
        anchorEl={iconAnchorEl}
        onClose={handlePopoverClose}
        PaperProps={{
          elevation: 0,
          sx: {
            p: '15px',
            borderRadius: '20px',
            border: '1px solid #E2E8F0',
            boxShadow: '0px 14px 40px 0px #6E9DB22E',
            maxWidth: '260px',
          },
        }}
        sx={{
          pointerEvents: 'none',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <ClassJoinTimeStatusViewerPopperComponent
          tutorJoinData={tutorJoinData}
          studentJoinData={studentJoinData}
          tutorJoinTime={section.tutorJoinTime}
          studentJoinTime={section.studentJoinTime}
        />
      </Popover>
      {isViewZoomRecordingsModalOpen ? (
        <ViewZoomRecordingsModal
          open={isViewZoomRecordingsModalOpen}
          onClose={toggleViewZoomRecordingsModal}
          zoomRecordings={section.zoomRecordings || []}
        />
      ) : null}
    </>
  );
};

export default ZoomSectionItem;
